import * as React from "react";
import { Alert, Card, Input, Spin } from "antd";
import { Link } from "react-router-dom";
import { tagLinkUrlPrefixes } from "../../services/utils";
import { TagLinksTable } from "../../components/detail/tagLinksTable";
import { CardContent } from "../../components/antd/card/CardContent";
import { Gvc } from "../../schema/types/gvc/entity";
import { FormItem } from "../../components/antd/form/FormItem";
import { FormLabel } from "../../components/antd/form/FormLabel";
import { FormItemValue } from "../../components/antd/form/FormItemValue";
import { CopyButtonWithApproval } from "../domain/create/copyButtonWithApproval";
import { CpFormItem } from "./testNewForm/CpFormItem";
import { CpLabel } from "./testNewForm/CpLabel";
import { CpErrorWarning } from "./testNewForm/CpErrorWarning";
import { CpTextArea } from "./testNewForm/CpTextArea";
import { CpLabelDirtyIndicator } from "./testNewForm/CpLabelDirtyIndicator";
import { linksOf, parentLink, request } from "../../services/cpln";
import { Workload } from "../../schema/types/workload/entity";
import { Identity } from "../../schema/types/identity/entity";
import { VolumeSet } from "../../schema/types/volumeSet/entity";
import { sortBy } from "lodash";
import { useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";

interface Props {
  gvc: Gvc;
}
interface GvcItemsListProps {
  label: string;
  items: { name: string; link: string }[];
  loading: boolean;
}

const INITIAL_DISPLAY_COUNT = 5;

const GvcItemsList: React.FC<GvcItemsListProps> = ({ label, items, loading }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const hasMore = items.length > INITIAL_DISPLAY_COUNT;
  const isSearching = search.length >= 3;
  const filteredItems = isSearching
    ? items.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()))
    : items;
  const displayedItems = expanded || isSearching ? filteredItems : filteredItems.slice(0, INITIAL_DISPLAY_COUNT);

  return (
    <FormItem>
      <FormLabel>
        {label} {!loading && `(${items.length})`}
      </FormLabel>
      {loading ? (
        <Spin size="small" />
      ) : items.length > 0 ? (
        <div className="flex flex-col gap-1">
          {hasMore && (
            <Input
              placeholder="Filter (min 3 chars)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ maxWidth: 250, marginBottom: 4 }}
            />
          )}
          {displayedItems.map((item) => (
            <Link key={item.link} to={`/console${item.link}`} style={{ color: "var(--color-link)", width: "fit-content" }}>
              {item.name}
            </Link>
          ))}
          {hasMore && !isSearching && (
            <span
              onClick={() => setExpanded(!expanded)}
              style={{ color: "var(--color-link)", cursor: "pointer" }}
            >
              {expanded ? "Show less" : `+${items.length - INITIAL_DISPLAY_COUNT} more`}
            </span>
          )}
          {isSearching && filteredItems.length === 0 && (
            <span style={{ color: "var(--color-label)" }}>No matches</span>
          )}
        </div>
      ) : (
        <FormItemValue>None</FormItemValue>
      )}
    </FormItem>
  );
};

export const Summary: React.FC<Props> = ({ gvc }) => {
  const org = useAppSelector(selectOrg);

  const [workloads, setWorkloads] = React.useState<Workload[]>([]);
  const [workloadsLoading, setWorkloadsLoading] = React.useState(true);

  const [identities, setIdentities] = React.useState<Identity[]>([]);
  const [identitiesLoading, setIdentitiesLoading] = React.useState(true);

  const [volumesets, setVolumesets] = React.useState<VolumeSet[]>([]);
  const [volumesetsLoading, setVolumesetsLoading] = React.useState(true);

  React.useEffect(() => {
    const ctx = { org, gvc: gvc.name };

    async function fetchWorkloads() {
      try {
        let nextLink: undefined | string = parentLink("workload", ctx);
        let items: Workload[] = [];
        while (nextLink) {
          const { data } = await request({ url: nextLink });
          items = items.concat(data.items);
          nextLink = linksOf(data).next;
        }
        setWorkloads(sortBy(items, (w) => w.name));
      } finally {
        setWorkloadsLoading(false);
      }
    }

    async function fetchIdentities() {
      try {
        let nextLink: undefined | string = parentLink("identity", ctx);
        let items: Identity[] = [];
        while (nextLink) {
          const { data } = await request({ url: nextLink });
          items = items.concat(data.items);
          nextLink = linksOf(data).next;
        }
        setIdentities(sortBy(items, (i) => i.name));
      } finally {
        setIdentitiesLoading(false);
      }
    }

    async function fetchVolumesets() {
      try {
        let nextLink: undefined | string = parentLink("volumeset", ctx);
        let items: VolumeSet[] = [];
        while (nextLink) {
          const { data } = await request({ url: nextLink });
          items = items.concat(data.items);
          nextLink = linksOf(data).next;
        }
        setVolumesets(sortBy(items, (v) => v.name));
      } finally {
        setVolumesetsLoading(false);
      }
    }

    fetchWorkloads();
    fetchIdentities();
    fetchVolumesets();
  }, [org, gvc.name]);

  const hasTagLinks =
    Object.entries(gvc.tags || {}).filter(([_, value]) => tagLinkUrlPrefixes.some((p) => String(value).startsWith(p)))
      .length > 0;

  return (
    <div className="cpln-form-section cpln-ant-card-container">
      <Card>
        <CardContent>
          <CpFormItem>
            <CpLabel name={`description`}>
              <FormLabel>Description</FormLabel>
              <CpLabelDirtyIndicator name={`description`} />
            </CpLabel>
            <CpTextArea autoSize name={`description`} style={{ maxWidth: 600 }} />
            <CpErrorWarning name={`description`} />
          </CpFormItem>
          <FormItem>
            <FormLabel>Alias</FormLabel>
            <div className="flex items-baseline gap-2">
              <FormItemValue>{gvc.alias}</FormItemValue>
              <CopyButtonWithApproval value={gvc.alias || ""} />
            </div>
          </FormItem>
          {gvc.spec.domain && (
            <FormItem>
              <FormLabel>Domain</FormLabel>
              <div className="flex items-baseline gap-2">
                <a
                  href={`https://${gvc.spec.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--color-link)" }}
                >
                  {gvc.spec.domain}
                </a>
                <CopyButtonWithApproval value={gvc.spec.domain} />
              </div>
              <Alert
                showIcon
                type="warning"
                className="mt-2 w-fit"
                message="This domain configuration method is deprecated. Please contact support to migrate to the new domain system."
              />
            </FormItem>
          )}
          <div className="flex gap-8" style={{ marginBottom: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <GvcItemsList
                label="Workloads"
                items={workloads.map((w) => ({ name: w.name, link: linksOf(w).self! }))}
                loading={workloadsLoading}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <GvcItemsList
                label="Identities"
                items={identities.map((i) => ({ name: i.name, link: linksOf(i).self! }))}
                loading={identitiesLoading}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <GvcItemsList
                label="Volume Sets"
                items={volumesets.map((v) => ({ name: v.name, link: linksOf(v).self! }))}
                loading={volumesetsLoading}
              />
            </div>
          </div>
          {hasTagLinks ? (
            <TagLinksTable
              tableId="gvc-info-tag-links"
              tags={Object.entries(gvc.tags || {})
                .filter(([_, value]) => tagLinkUrlPrefixes.some((p) => String(value).startsWith(p)))
                .map(([key, value]) => ({ key, value }))}
              styles={{ container: { width: "100%" }, header: { marginBottom: 0 } }}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
